import { waitForProp } from '@ribajs/utils'
import { loadScript } from '../utils/index.js'
import { AnalyticsService } from './analytics.service.js'
import { WindowShopify } from '../types/window-shopify.js'

/**
 * Service to handle the shopify customer privacy and cookies banner
 * @see https://shopify.dev/docs/api/customer-privacy
 */
class ShopifyConsentService {
  analytics?: AnalyticsService

  consent = {
    analytics: false,
    marketing: false,
    preferences: false,
    sale_of_data: false,
  }

  /** Enable this to enable debug messages */
  enableDebug = false

  constructor() {
    this.debug('ShopifyConsentService constructor')
    document.addEventListener(
      'visitorConsentCollected',
      this.onVisitorConsentCollected.bind(this),
    )
    this.init()
  }

  debug(...args: any[]) {
    if (this.enableDebug) {
      console.debug('[ShopifyConsentService]', ...args)
    }
  }

  async init() {
    this.#initAblyft()

    const shopify = await waitForProp<WindowShopify>('Shopify', window)
    shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1',
        },
      ],
      async (error: unknown) => {
        if (error) {
          console.error(
            `[ShopifyConsentService]`,
            'Could not load consent-tracking-api',
          )
          return
        }

        await waitForProp('customerPrivacy', window.Shopify)
        await this.onVisitorConsentCollected()
      },
    )
  }

  currentVisitorConsent() {
    const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent()
    return {
      analytics: consent?.analytics === 'yes',
      marketing: consent?.marketing === 'yes',
      preferences: consent?.preferences === 'yes',
      sale_of_data: consent?.sale_of_data === 'yes',
    }
  }

  async onVisitorConsentCollected() {
    const { analytics, marketing, preferences, sale_of_data } =
      this.currentVisitorConsent()

    const analyticsChanged = this.consent?.analytics !== analytics
    const marketingChanged = this.consent?.marketing !== marketing
    const preferencesChanged = this.consent?.preferences !== preferences
    const saleOfDataChanged = this.consent?.sale_of_data !== sale_of_data

    this.debug('onVisitorConsentCollected', {
      analyticsChanged,
      marketingChanged,
      preferencesChanged,
      saleOfDataChanged,
      analytics,
      marketing,
      preferences,
      sale_of_data,
    })

    if (analyticsChanged) {
      if (analytics) {
        await this.onAnalyticServicesAllowed()
      } else {
        await this.onAnalyticsDisallowed()
      }
      this.consent.analytics = analytics
    }

    if (marketingChanged) {
      if (marketing) {
        await this.onMarketingAllowed()
      } else {
        await this.onMarketingDisallowed()
      }
      this.consent.marketing = marketing
    }

    if (preferencesChanged) {
      if (preferences) {
        await this.onPreferencesAllowed()
      } else {
        await this.onPreferencesDisallowed()
      }
      this.consent.preferences = preferences
    }

    if (saleOfDataChanged) {
      if (sale_of_data) {
        await this.onSaleOfDataAllowed()
      } else {
        await this.onSaleOfDataDisallowed()
      }
      this.consent.sale_of_data = sale_of_data
    }
  }

  /**
   * Initialize third party analytics services
   * @returns {Promise<void>}
   */
  async onAnalyticServicesAllowed(): Promise<void> {
    this.debug('onAnalyticServicesAllowed')

    // this.#initGtm()
  }

  // async #initGtm() {
  //   this.debug('Init GTM...')
  //   if (this.analytics || AnalyticsService.hasInstance()) {
  //     console.warn('GTM already initialized')
  //     return
  //   }
  //   this.analytics = AnalyticsService.setInstance(
  //     // General settings
  //     {
  //       debug: window.debug,
  //     },
  //     // GTM settings
  //     {
  //       // containerId: 'GTM-WSZPXWN' // Patricks Testaccount
  //       // containerId: 'GTM-TZBS978', // Pascal Testaccount
  //       // containerId: 'GTM-WXWZMS4' // Seeberger
  //     },
  //   )
  // }

  async #initAblyft() {
    this.debug('Init ABlyft...')
    try {
      await loadScript('https://cdn.ablyft.com/s/53273492.js', {
        id: 'ablyft',
        type: 'application/javascript',
      })
    } catch (error) {
      console.error('Error loading ABlyft', error)
    }
  }

  async onAnalyticsDisallowed(): Promise<void> {
    this.debug('onAnalyticsDisallowed')
    if (this.analytics) {
      this.analytics.destroy()
      this.analytics = undefined
    }
  }

  async onMarketingAllowed(): Promise<void> {
    this.debug('onMarketingAllowed')
    try {
      await loadScript(
        'https://integrations.etrusted.com/applications/widget.js/v2',
        {
          id: 'etrusted',
          async: true,
          defer: true,
          type: 'application/javascript',
        },
      )
    } catch (error) {
      console.error('Error loading eTrusted', error)
    }
  }

  async onMarketingDisallowed(): Promise<void> {
    this.debug('onMarketingDisallowed')
    window.location.reload()
  }

  async onPreferencesAllowed(): Promise<void> {
    this.debug('onPreferencesAllowed')
  }

  async onPreferencesDisallowed(): Promise<void> {
    this.debug('onPreferencesDisallowed')
    window.location.reload()
  }

  async onSaleOfDataAllowed(): Promise<void> {
    this.debug('onSaleOfDataAllowed')
  }

  async onSaleOfDataDisallowed(): Promise<void> {
    this.debug('onSaleOfDataDisallowed')
    window.location.reload()
  }
}

// Export singleton only
export const shopifyConsentService = new ShopifyConsentService()
